export const shortcutsArticle = {
    id: 'shortcuts-tips',
    title: 'Горячие клавиши системы',
    category: 'Горячие клавиши',
    requiredModule: null,
    summary: 'Полный справочник быстрых клавиш: переключение режимов, выбор инструментов, рисование, правка вершин и топология.',
    markdown: `
> **Интерактивная карта горячих клавиш**: нажимайте любые клавиши на клавиатуре или кликайте по клавишам на схеме ниже — система подсветит соответствующую кнопку и покажет подробное описание действия, режим и полезные приемы.

{{keyboard:interactive}}

---

## 1. Быстрое переключение режимов (Workspaces)

Нажимайте цифры на клавиатуре для мгновенного перехода между рабочими пространствами:

| Клавиша | Режим | Назначение |
| :---: | :--- | :--- |
| **\`1\`** | **Рисование** | Нанесение участков, зданий, линий и меток на карту |
| **\`2\`** | **Данные** | Измерения (линейка, площадь, координаты), реестры и легенда |
| **\`3\`** | **Расширение** | Дополнительные модули расширения, экспорт и печать |

---

## 2. Быстрый выбор инструментов (Эргономичная клавиатурная сетка)

Инструменты сгруппированы по рядам клавиатуры для удобной работы вслепую (поддерживаются английская и русская раскладки):

:::grid cols=3
:::card icon="square" title="Ряд Q-W-E-R-T: Рисование"
* **\`Q\`** *(или \`Й\`)* — **Участки** (границы)
* **\`W\`** *(или \`Ц\`)* — **Здания** (строения)
* **\`E\`** *(или \`У\`)* — **Линии** (дороги, сети)
* **\`R\`** *(или \`К\`)* — **Метки** (ориентиры)
* **\`T\`** *(или \`Е\`)* — **Подложка** (планы)
:::

:::card icon="activity" title="Ряд A-S-D-F-G: Данные"
* **\`A\`** *(или \`Ф\`)* — **Линейка** (расстояние)
* **\`S\`** *(или \`Ы\`)* — **Площадь** (замер контура)
* **\`D\`** *(или \`В\`)* — **Координаты** точки
* **\`F\`** *(или \`А\`)* — **Менеджер объектов**
* **\`G\`** *(или \`П\`)* — **Легенда категорий**
:::

:::card icon="zap" title="Ряд Z-X-C-V: Действия"
* **\`Z\`** *(или \`Я\`)* — **Отмена (Undo)**
* **\`X\`** *(или \`Ч\`)* — **Сброс / Выбор**
* **\`C\`** *(или \`С\`)* — **Повтор (Redo)**
* **\`V\`** *(или \`М\`)* — **Экспорт данных**
:::
:::

---

## 3. Режим создания объектов (Рисование)

| Комбинация | Действие | Описание |
| :--- | :--- | :--- |
| **Shift** *(удержание)* | Ортогональная привязка | Строгие углы 90° и 45°, автодоводка 4-го угла прямоугольника |
| **Ctrl + Z** / **Backspace** | Отмена точки | Пошаговое удаление последней зафиксированной точки контура |
| **Enter** / Двойной клик | Завершить чертёж | Замкнуть контур участка и открыть Паспорт объекта |
| **Esc** | Выход из рисования | Сбросить незавершенный чертеж и выйти в режим выбора |

---

## 4. Режим «Правка геометрии» (изменение вершин)

| Комбинация | Действие | Описание |
| :--- | :--- | :--- |
| **Двойной клик по ребру** | Добавить вершину | Создает новую узловую точку в контуре |
| **Перетаскивание узла** | Изменить форму | Перемещает существующую вершину контура мышью |
| **Delete** / **Backspace** | Удалить вершину | Удаляет выделенную узловую точку контура |
| **Ctrl + Z** | Откатить шаг | Отменяет перемещение или добавление вершины |
| **Ctrl + Y** / **Ctrl+Shift+Z** | Повторить шаг | Повторяет отмененное действие с вершиной |
| **Enter** | Сохранить | Зафиксировать обновленную геометрию в проекте |
| **Esc** | Отмена | Прервать правку и восстановить исходные границы |

---

## 5. Топология, дублирование и история (модуль SPD+)

| Комбинация | Действие | Описание |
| :--- | :--- | :--- |
| **Ctrl + D** | Дублирование участка | Быстрое создание копии выделенного участка |
| **Shift** *(при дублировании)* | Магнитная стыковка встык | Автоматическая пристыковка дубликата «ребро-в-ребро» по 4 сторонам |
| **Shift + Клик** | Множественный выбор | Выделение нескольких участков для вырезания, слияния или пакетной смены данных |
| **Ctrl + Z** | Откат топологии | Мгновенный откат операций вычитания, объединения и дублирования |
| **Ctrl + Y** / **Ctrl+Shift+Z** | Повтор топологии | Повтор отмененной топологической операции |

---

## 6. Навигация по карте и системные действия

| Комбинация | Действие | Описание |
| :--- | :--- | :--- |
| **Колесо мыши** / **\`+\`** **\`-\`** | Масштаб (Zoom) | Плавное приближение и отдаление карты |
| **Правая кнопка мыши** | Вращение (Bearing) | Вращение карты по азимуту |
| **Shift + Правая кнопка** | Наклон 3D (Pitch) | Изменение угла наклона камеры для трехмерного обзора |
| **Ctrl + K** / **\`/\`** | Поиск по документации | Открытие окна быстрого поиска по базе знаний |
| **\`?\`** / **F1** | Справка | Быстрый переход в руководство пользователя |
| **Esc** | Закрытие панелей | Закрытие модальных окон, инспекторов и паспорта объекта |
`
};
